import {
  FileText,
  Gauge,
  Globe,
  Lightbulb,
  LineChart,
  Linkedin,
  ListChecks,
  ScrollText,
  Search,
  ShieldAlert,
  Table2,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";

/**
 * The reports a lookbook can publish, keyed the way the backend keys them.
 *
 * Deliberately separate from REPORT_ITEMS in components/deal-detail: that
 * registry is the *internal* deal page's view, keyed by its own ids and merged
 * virtual keys (`tearsheets_all` spans two storage shapes), and every entry
 * carries generate/delete callbacks that pull `aiApi` — and with it the whole
 * authenticated API layer — into anything that imports it. The public lookbook
 * page must not import that.
 *
 * The two lists are also genuinely different sets: this one is the server's
 * share allowlist, which excludes the fund's own thesis scoring (deal fit) and
 * the internal Q&A thread (chats). Keep it in step with SHAREABLE_REPORT_KEYS
 * in startupfuel/utils/lookbook_sharing.ts — that file is the source of truth
 * and this one only decides how the allowed keys are labelled and iconed. A key
 * missing here still renders, under its raw name, which is the reason to keep
 * them in step rather than the thing that breaks if you don't.
 */
export interface LookbookReportMeta {
  key: string;
  title: string;
  description: string;
  icon: LucideIcon;
}

export const LOOKBOOK_REPORT_CATALOG: LookbookReportMeta[] = [
  {
    key: "tearsheets_v2",
    title: "Tear Sheet",
    description: "One-page executive snapshot",
    icon: FileText,
  },
  {
    key: "tearsheets",
    title: "Tear Sheet",
    description: "One-page executive snapshot",
    icon: FileText,
  },
  {
    key: "summaries",
    title: "Deal Brief",
    description: "Diligence evaluation summary",
    icon: ScrollText,
  },
  {
    key: "metrics",
    title: "Metrics",
    description: "Financial metrics and KPIs",
    icon: Table2,
  },
  {
    key: "startupscores",
    title: "Business Risk",
    description: "Risk assessment across categories",
    icon: ShieldAlert,
  },
  {
    key: "market_intel",
    title: "Market Intelligence",
    description: "Market landscape and positioning",
    icon: TrendingUp,
  },
  {
    key: "market_research",
    title: "Market Research",
    description: "Researched market sizing and trends",
    icon: Search,
  },
  {
    key: "dealMemo",
    title: "Deal Memo",
    description: "Investment memorandum",
    icon: FileText,
  },
  {
    key: "valuations",
    title: "Valuations",
    description: "Valuation estimates and comparables",
    icon: LineChart,
  },
  {
    key: "website_summary_v2",
    title: "Website Summary",
    description: "What the company's site says",
    icon: Globe,
  },
  {
    key: "websiteInfo",
    title: "Website Analysis",
    description: "Analysis of the company's web presence",
    icon: Globe,
  },
  {
    key: "linkedInInfo",
    title: "LinkedIn Insights",
    description: "Company profile and headcount signals",
    icon: Linkedin,
  },
  {
    key: "insights",
    // Not just "Insights": "LinkedIn Insights" is already a row, and two rows
    // an adjective apart is a worse read than one extra word.
    title: "Diligence Insights",
    description: "What diligence asked, and what it found",
    icon: Lightbulb,
  },
  {
    key: "gap_analysis",
    title: "Gap Analysis",
    description: "Which diligence questions are still unanswered",
    icon: ListChecks,
  },
  {
    key: "datasufficiencyscores_v2",
    title: "Data Sufficiency",
    description: "How complete the deal's information is",
    icon: Gauge,
  },
  {
    key: "datasufficiencyscores",
    title: "Data Sufficiency",
    description: "How complete the deal's information is",
    icon: Gauge,
  },
];

const BY_KEY = new Map(LOOKBOOK_REPORT_CATALOG.map((r) => [r.key, r]));

/** Metadata for a report key, or a readable fallback for one we don't know. */
export const lookbookReportMeta = (key: string): LookbookReportMeta =>
  BY_KEY.get(key) ?? {
    key,
    title: key,
    description: "",
    icon: FileText,
  };
